import {useCountryList} from '@/context/CountryListContext';
import type {AdminAfriexPerson} from '@/types';
import {BusinessKYCDocumentType} from '@/types/BusinessKYCDocumentType';
import {mapCountryCodeToFlags} from '@/utils/countries';

import KYCImage from './KYCImage';

type KYCSummaryProps = {user: AdminAfriexPerson; extraRow?: React.ReactNode};

// Prefer the address the KYC provider returned whole, fall back to the one we
// assemble from its parts, and show a dash when neither has any content.
const resolveDisplayAddress = (
  kycFullAddress: string | undefined,
  formattedUserAddress: string,
) => {
  if (kycFullAddress && kycFullAddress.trim() !== '') {
    return kycFullAddress;
  }
  if (formattedUserAddress.trim() !== '') {
    return formattedUserAddress;
  }
  return '-';
};

const Summary = ({user, extraRow}: KYCSummaryProps) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const country = user?.currentCountry?.toUpperCase() ?? '-';
  const veriffSelfie = user?.kyc?.images?.selfie;
  const sumSubSelfie = user?.kyc?.media?.images?.length
    ? user.kyc.media.images[user.kyc.media.images.length - 1]
    : undefined;
  const selfieDoc = (user?.kyc?.docs ?? [])?.find(
    (doc) => doc?.idType === BusinessKYCDocumentType.Selfie,
  );
  const docSelfie = selfieDoc?.media?.source ?? '';

  const formattedUserAddress = [
    user?.kyc?.address?.line1,
    user?.kyc?.address?.line2,
    user?.kyc?.address?.city,
    user?.kyc?.address?.state,
    user?.kyc?.address?.country,
  ]
    .filter(Boolean)
    .join(', ');

  const displayedAddress = resolveDisplayAddress(
    user?.kyc?.address?.fullAddress,
    formattedUserAddress,
  );

  return (
    <div className="px-3 pt-3 my-8">
      <h3 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
        {'Summary'.toLocaleUpperCase()}
      </h3>
      <hr className="mb-5 dark:border-dark-borderMid" />

      <div className="flex flex-col md:flex-row bg-white dark:bg-transparent rounded-lg w-full px-5 py-5 gap-5 border border-gray-200 dark:border-dark-borderMid">
        <div className="w-full md:w-1/4">
          <KYCImage
            src={sumSubSelfie || veriffSelfie || docSelfie}
            alt="User selfie"
            name={user?.name?.fullName}
          />
        </div>
        <div className="bg-white dark:bg-transparent rounded-lg w-full">
          <div className="flex items-center gap-4 mb-3">
            <h2 className="font-semibold text-2xl mr-3">
              {user.name?.fullName}
            </h2>
          </div>

          <div>
            {/* Country */}
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Country
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                {`${country} ${mapCountryCodeToFlags(countryList)[country] ?? ''}`}
              </p>
            </div>

            {/* Date of Birth */}
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Date of Birth
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                {user?.kyc?.dateOfBirth || '-'}
              </p>
            </div>

            {/* Verification Status */}
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Verification
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                {user?.kyc?.status?.toUpperCase()}
              </p>
            </div>

            {/* UserID */}
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                User ID
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 truncate">
                {user.id}
              </p>
            </div>

            {/* Address */}
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Address
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                {displayedAddress}
              </p>
            </div>
          </div>

          {extraRow || ''}
        </div>
      </div>
    </div>
  );
};

export default Summary;
